import { es } from "date-fns/locale";
import { useFormik } from "formik";
import { useAtom } from "jotai";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { toast } from "sonner";
import * as yup from "yup";
import { formsSubmitAtom } from "../../../atom/atom";
import CustomSelect from "../../../components/forms/CustomSelect";
import { DatePicker } from "../../../components/forms/DatePicker";
import RequiredInput from "../../../components/forms/InputHOC";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { VehicleService } from "../../../services/vehicles/vehicleService";
import { Vehicle } from "../../../type/entities/vehicle-type";
import { vehicleStatusesSpanish } from "../../../utils/SelectorMapping";
import { Switch } from "rizzui";

interface VehicleFormProps {
    setOpenModal: any;
    refetch: () => void;
    vehicle?: Vehicle | null;
    formRef?: any;
}

const schema = yup.object({
    plateNumber: yup.string().required('La placa es obligatoria'),
    brand: yup.string().required('La marca es obligatoria'),
    model: yup.string().notRequired(),
    status: yup.string().required('El estado es obligatorio'),
    purchaseDate: yup.string().notRequired(),
    saleDate: yup.string().nullable(),
    comments: yup.string().nullable(),
    wildcard: yup.boolean().required('El estado es obligatorio'),
    registrationDate: yup.string().notRequired(),
    power: yup.number().nullable(),
    cylinderCapacity: yup.number().nullable(),
    fuelType: yup.string().notRequired(),
    owner: yup.string().notRequired(),
    company: yup.string().required('La empresa es obligatoria'),
})

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La empresa es obligatoria'),
}))


const VehicleForm: FC<VehicleFormProps> = ({ setOpenModal, refetch, vehicle, formRef }) => {

    // STATES

    const service = new VehicleService();
    const isEdit = vehicle !== null;
    const [isSearched, setIsSearched] = useState<boolean>(false);

    // HOOKS

    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { userCan } = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: vehicle?.id ?? '',
            plateNumber: vehicle?.plateNumber ?? '',
            brand: vehicle?.brand ?? '',
            model: vehicle?.model ?? '',
            status: isEdit ? vehicle?.status ?? '' : 'available',
            purchaseDate: vehicle?.purchaseDate ? moment(vehicle.purchaseDate?.date).format('YYYY-MM-DD') : '',
            saleDate: vehicle?.saleDate ? moment(vehicle.saleDate?.date).format('YYYY-MM-DD') : '',
            comments: vehicle?.comments ?? '',
            company: vehicle?.company?.id ?? '',
            wildcard: vehicle?.wildcard ?? false,
            registrationDate: vehicle?.registrationDate ? moment(vehicle.registrationDate?.date).format('YYYY-MM-DD') : '',
            power: vehicle?.power ?? null,
            cylinderCapacity: vehicle?.cylinderCapacity ?? null,
            fuelType: vehicle?.fuelType ?? '',
            owner: vehicle?.owner ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN NUEVO VEHÍCULO
     * @EN CREATE A NEW VEHICLE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createVehicle(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Vehículo creado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN VEHÍCULO
     * @EN EDIT A VEHICLE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const response = await (await service.editVehicle(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Vehículo editado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECT

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>

                        <RequiredInput
                            id='plateNumber'
                            label="Matrícula"
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.plateNumber}
                        />

                        <RequiredInput
                            id='brand'
                            label="Marca"
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.brand}
                        />

                        <RequiredInput
                            required={false}
                            id='model'
                            label="Modelo"
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.model}
                        />

                        <CustomSelect
                            required
                            isSearchable
                            id={'status'}
                            label="Estado"
                            containerClassName='md:col-span-6'
                            value={{ value: formik.values.status, label: vehicleStatusesSpanish.find((g: any) => g.value === formik.values.status)?.label }}
                            options={vehicleStatusesSpanish.filter((g: any) => g.value !== 'sold')}
                            onChange={
                                (e: any) => { formik.setFieldValue('status', e?.value) }
                            }
                            isDisabled={!isEdit}
                            error={formik.errors.status}
                        />

                        <div className="col-span-12">
                            <span className="block text-sm font-medium text-gray-700">Fecha de compra</span>
                            <DatePicker
                                monthsShown={1}
                                label="Fecha de compra"
                                placeholderText="Selecciona una fecha"
                                inputProps={{
                                    inputClassName: 'h-9 [&_input]:text-ellipsis bg-white',
                                }}
                                selected={formik.values.purchaseDate ? moment(formik.values.purchaseDate).toDate() : null}
                                onChange={(date: any) => {
                                    formik.setFieldValue('purchaseDate', date ? moment(date).format('YYYY-MM-DD') : '');
                                }}
                                error={formik.errors.purchaseDate}
                                locale={es}
                                required={false}
                            />
                        </div>

                        <div className="col-span-12">
                            <span className="block text-sm font-medium text-gray-700">Fecha de matriculación</span>
                            <DatePicker
                                monthsShown={1}
                                label="Fecha de matriculación"
                                placeholderText="Selecciona una fecha"
                                inputProps={{
                                    inputClassName: 'h-9 [&_input]:text-ellipsis bg-white',
                                }}
                                selected={formik.values.registrationDate ? moment(formik.values.registrationDate).toDate() : null}
                                onChange={(date: any) => {
                                    formik.setFieldValue('registrationDate', date ? moment(date).format('YYYY-MM-DD') : '');
                                }}
                                error={formik.errors.registrationDate}  
                                locale={es}
                                required={false}
                            />
                        </div>
                        
                        <RequiredInput
                            required={false}
                            id='owner'
                            label="Propietario"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.owner}
                        />

                        <RequiredInput
                            required={false}
                            id='power'
                            label="Potencia"
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.power}
                        />

                        <RequiredInput
                            required={false}
                            id='cylinderCapacity'
                            label="Cilindrada"
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.cylinderCapacity}
                        />

                        <RequiredInput
                            required={false}
                            id='fuelType'
                            label="Tipo de combustible"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.fuelType}
                        />
                        <Switch
                            required={false}
                            id='wildCard'
                            label="Vehículo comodín"
                            className='md:col-span-12'
                            checked={formik.values.wildcard}
                            onChange={(e: any) => {
                                formik.setFieldValue('wildcard', e.target.checked);
                            }}
                            error={formik.errors.wildcard}
                        />

                        {userCan('list', 'company') && (
                            <CustomSelect
                                isSearchable
                                required
                                containerClassName="col-span-12"
                                id="companyId"
                                label="Compañía"
                                placeholder="Selecciona una compañía"
                                options={getCompaniesList()}
                                value={
                                    formik.values.company ?
                                    { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                    getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                                }
                                onChange={(selectedOption: any) => {
                                    formik.setFieldValue('company', selectedOption?.value || '');
                                }}
                                error={formik.touched.company && formik.errors.company ? (typeof formik.errors.company === 'string' ? formik.errors.company : '') : ''}
                            />
                        )}

                    </div>
                </div>
            </form>
        </div>
    );
};

export default VehicleForm;